<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex flex-column">
        <div class="am-h2">Création d'une association</div>
        <template v-if="!tribunal">
          <div class="accent-block mt-4 mb-4">
            <div class="am-t2">
              Vous souhaitez créer votre association en Alsace Moselle.
            </div>
            <div class="am-t2">Merci de compléter le formulaire ci-dessous en naviguant via les différents onglets.</div>
          </div>
          <p class="am-t2">
            Afin de vous accompagner dans vos démarches vous pouvez consulter le guide
            suivant :
            <a
              target="_blank"
              :href="lienGuideAMA"
            >
              Créer une association en Alsace - Moselle</a
            >
          </p>
          <p class="am-t2">
            Avant tout dépôt, merci de vérifier la conformité de votre déclaration avec
            les articles 21 à 79-XII du code civil local (La loi 1901 ne s'applique pas
            aux associations dont le siège se situe dans les départements du Bas-Rhin, du
            Haut-Rhin et de la Moselle).
          </p>
        </template>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-fade-transition mode="out-in">
          <a-fin-creation-sheet v-if="tribunal" :projection="tribunal" />
          <v-stepper v-else alt-labels v-model="step">
            <v-stepper-header>
              <v-stepper-step
                v-for="s of stepsEditable"
                edit-icon="$complete"
                :key="s.step + 's'"
                :step="s.step"
                :editable="s.editable && !working"
                :rules="s.rules"
              >
                {{ s.text }}
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content :step="1">
                <v-form ref="declarant" autocomplete="false">
                  <a-declarant-sheet v-model="item.declarant" creation />
                  <div class="d-flex justify-center mt-2">
                    <v-btn data-testid="suivant" @click="suivant('declarant')" class="primary"> Suivant </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
              <v-stepper-content :step="2">
                <v-form ref="association" autocomplete="false">
                  <a-association-sheet v-model="item.association" :libelleJuridiction="libelleJuridiction"/>
                  <div class="d-flex justify-center mt-2">
                    <v-btn data-testid="suivant" @click="suivant('association')" class="primary">
                      Suivant
                    </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
              <v-stepper-content :step="3">
                <v-form ref="membres">
                  <a-membres-sheet v-model="item.membres" @disabled="updateMembres" />
                  <div class="d-flex justify-center">
                    <v-btn
                    data-testid="suivant"
                      @click="suivant('membres')"
                      class="primary"
                      :disabled="membresDisabled"
                    >
                      Suivant
                    </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
              <v-stepper-content :step="4">
                <v-form ref="annexes">
                  <a-annexe-sheet
                    v-model="item.annexes"
                    @clearValidation="clearAnnexeDocument"
                    @validate="validateAnnexes"
                    @disabled="updateAnnexes"
                  />
                  <div class="d-flex justify-center">
                    <v-btn
                    data-testid="suivant"
                      @click="suivant('annexes')"
                      class="primary"
                      :disabled="annexesDisabled"
                    >
                      Suivant
                    </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
              <v-stepper-content :step="5">
                <v-form ref="validation" :readonly="working">
                  <div class="accent-block mt-4 mb-4">
                    <p>
                        {{libelleJuridiction}}
                    </p>
                    <p>
                      Merci de vérifier vos informations avant de soumettre votre
                      formulaire.
                    </p>
                  </div>
                  <v-textarea
                    label="Observation"
                    filled
                    v-model="item.commentaire"
                    hint="Pour toute information complémentaire"
                    persistent-hint
                    :rules="[serverRule('commentaire')]"
                    data-testid="observation"
                  />
                  <div class="d-flex justify-center mt-2">
                    <v-btn
                    data-testid="soumettre"
                      class="primary"
                      @click="soumettre"
                      :disabled="annexesDisabled"
                      :loading="working"
                    >
                      Soumettre
                    </v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-fade-transition>
      </v-col>
    </v-row>
    <a-confirm-navigation-dialog ref="confirm-navigation" />
  </v-container>
</template>

<script>
import makeRemoteWorker from "@/mixins/makeRemoteWorker";
import makeNavigationGuard from "@/mixins/makeNavigationGuard";
import { requeteService } from "@/services";
import copy from "@/services/copy";
import makeRules from "@/mixins/makeRules";
import { mapGetters } from "vuex";

export default {
  mixins: [makeRemoteWorker(), makeNavigationGuard(), makeRules({ inject: false })],

  data() {
    return {
      item: {
        declarant: {},
        association: {},
        annexes: { annexes: [] },
        membres: {
          comite: {
            physiques: [],
            morales: [],
          },
          fondateurs: {
            physiques: [],
            morales: [],
          },
        },
        commentaire: null,
      },
      membresDisabled: true,
      annexesDisabled: true,
      tribunal: null,
      step: 1,
      maxStep: 0,
      steps: [
        { text: "Déclarant", rules: [this.stepperRule("declarant")] },
        { text: "Association", rules: [this.stepperRule("association")] },
        {
          text: "Membres",
          rules: [
            this.stepperRule("membres"),
            this.stepperRule("comite"),
            this.stepperRule("fondateurs"),
          ],
        },
        { text: "Annexes", rules: [this.stepperRule("documents"), this.annexesRule()] },
        { text: "Soumission" },
      ].map(({ text, rules }, index) => ({
        step: index + 1,
        text,
        rules,
      })),
    };
  },
  mounted() {
    this.$nextTick(() => this.initSnapshot());
  },
  computed: {
     ...mapGetters({
      lienGuideAMA: "fichiers/lienGuideAMA",
    }),
    stepsEditable() {
      return this.steps.map((s, index) => ({
        ...s,
        editable: index < this.step,
      }));
    },
    annexesIndex() {
      return this.steps?.find(s => s.text === "Annexes")?.step - 1;
    },
    libelleJuridiction() {
      var association = this.item.association;
      var commune = association.adresse?.commune;
      if (commune) {
        const juridiction = association.cooperative
          ? commune.tribunalJudiciaire
          : commune.tribunalProximite;
        return `La juridiction compétente pour traiter votre demande est le ${juridiction}.`;
      }
      return null;
    },
  },

  methods: {
    updateMembres(disabled) {
      this.membresDisabled = disabled;
    },
    updateAnnexes(disabled) {
      this.annexesDisabled = disabled;
    },
    annexesRule() {
      return () => {
        if (this.annexesIndex < this.maxStep) {
          return !this.annexesDisabled;
        }
        return true;
      };
    },
    stepperRule(prefix) {
      return () => !Object.keys(this.validation?.fields).some(k => k.startsWith(prefix));
    },
    suivant(form) {
      this.clearValidation(form);

      if (this.$refs[form].validate()) {
        this.maxStep = Math.max(this.step, this.maxStep);
        this.step = this.step + 1;
      }
    },
    clearAnnexeDocument(index) {
      this.nettoyeValidation(f => f.startsWith(`documents[${index}]`));
      this.$refs["annexes"].validate();
    },
    clearValidation(form) {
      if ("declarant" === form) {
        this.nettoyeValidation(f => f.startsWith("declarant"));
      } else if ("association" === form) {
        this.nettoyeValidation(f => f.startsWith("association"));
      } else if ("membres" === form) {
        this.nettoyeValidation(f =>
          ["membres", "comite", "fondateurs"].some(v => f.startsWith(v))
        );
      } else if ("annexes" === form) {
        this.nettoyeValidation(f => f.startsWith("documents"));
      }
    },
    validateAnnexes() {
      this.$refs.annexes.validate();
    },
    prepareInformations(association) {
      const dureeValidite = association?.dureeValidite;
      const dureeRenouvellement = association?.dureeRenouvellement;
      return {
        ...association,
        dureeValidite: dureeValidite?.valeur ? dureeValidite : null,
        dureeRenouvellement: dureeRenouvellement?.valeur ? dureeRenouvellement : null,
      };
    },
    soumettre() {
      const association = this.prepareInformations(copy(this.item.association));
      const item = {
        declarant: this.item.declarant,
        association,
        annexes: this.item.annexes.annexes,
        membres: this.item.membres,
        commentaire: this.item.commentaire,
      };
      this.doWork({
        forms: [
          this.$refs.declarant,
          this.$refs.association,
          this.$refs.membres,
          this.$refs.annexes,
          this.$refs.validation,
        ],
        work() {
          return requeteService.creer(item);
        },
        callback(value) {
          this.initSnapshot();
          this.tribunal = value;
        },
        successText: "La requête a été créée",
        errorText: "Erreur durant la création de la requête",
      });
    },
  },
};
</script>
